import { setLoading, setLoadingUnique } from '../../index';
import {
  getLineItemsOfSelectedOrder,
  getOrderingSystem,
  getOrderModificationsAreAllowed,
  getSelectedLineItems,
  getSelectedLineItemsIds,
  getSelectedOrderId,
  getECommerceSearchEmail,
  getSelectedOrder,
} from '../../../selectors/tools';
import { getDecisionNodeAtPath, getSimplrAccountIdOfSelectedTicket } from '../../../extractFromState';
import * as ordersActions from '../OrdersSearchActions';
import { showErrorPopup } from '../../../components/popups/ErrorPopup/ErrorPopup';
import { canCancel, canDiscount, canRefund } from '../../../api/orders';
import { getItemId, getItemIneligiblityReason, getItemName } from '../../../util';
import { replaceTemplatedTextForMultipleItems } from '../../../selectors/templating';
import { addEdgeToDecisionTreePath, addSnippetText, setInvalidSnippetData } from '../../searchActions';
import { CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES } from '../../../constants/decisionTree';
import { showError } from '../../../util/errorPopupUtil';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { handleError } from 'services/ErrorService/ErrorService';

function getIsItemEligibleForAction(response, itemId) {
  return response[itemId]?.canPerformActionValue;
}

export const processRemotelyForCancellation = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));

      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const orderId = getSelectedOrderId(state);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
      const selectedItemsIds = getSelectedLineItemsIds(state);
      const searchEmail = getECommerceSearchEmail(state);
      const accountType = get(getSelectedOrder(state), 'extraParams.vendorType', null);

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // 3. Get order cancel info

      const payload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(searchEmail && { email: searchEmail, customerEmail: searchEmail }),
        ...(accountType && { accountType }),
      };

      const cancelData = (await canCancel(payload)) || {};

      // 4. Process each item to determine if they qualify for a cancellation and refund
      const validSnippetData = [];
      const invalidSnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemId = getItemId(item, orderingSystem);

        if (getIsItemEligibleForAction(cancelData, itemId)) {
          dispatch(ordersActions.addCancelOnlyItem(item));
          validSnippetData.push(itemSnippetData);
        } else {
          invalidSnippetData.push(itemSnippetData);
        }
      });

      if (validSnippetData.length) {
        const validSnippet = replaceTemplatedTextForMultipleItems(decisionNode.itemValidSnippet, validSnippetData);
        validSnippet && dispatch(addSnippetText(validSnippet));
      }

      if (invalidSnippetData.length) {
        const invalidSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidSnippet,
          invalidSnippetData
        );
        invalidSnippet && dispatch(addSnippetText(invalidSnippet));
      }

      if (Boolean(validSnippetData.length)) {
        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (e) {
      showError(e);
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const processRemotelyForCancellationOrDiscount = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoadingUnique(true, 'searchPage'));

      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const orderId = getSelectedOrderId(state);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
      const selectedItemsIds = getSelectedLineItemsIds(state);
      const searchEmail = getECommerceSearchEmail(state);
      const accountType = get(getSelectedOrder(state), 'extraParams.vendorType', null);

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // 3. Get order cancel and refund info
      const payload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(searchEmail && { email: searchEmail, customerEmail: searchEmail }),
        ...(accountType && { accountType }),
      };

      const discountPayload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(searchEmail && { searchEmail, customerEmail: searchEmail }),
        ...(accountType && { accountType }),
      };

      const cancelData = (await canCancel(payload)) || {};
      const discountData = (await canDiscount(discountPayload)) || {};

      // 4. Process each item to determine if they qualify for a cancellation or discount
      const cancelOnlySnippetData = [];
      const invalidSnippetData = [];
      const discountOnlySnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemId = getItemId(item, orderingSystem);
        const itemIsEligibleForCancel = getIsItemEligibleForAction(cancelData, itemId);
        const itemIsEligibleForDiscount = getIsItemEligibleForAction(discountData, itemId);

        if (itemIsEligibleForDiscount) {
          dispatch(ordersActions.addDiscountOnlyItem(item));
          discountOnlySnippetData.push(itemSnippetData);
        } else if (itemIsEligibleForCancel) {
          dispatch(ordersActions.addCancelOnlyItem(item));
          cancelOnlySnippetData.push(itemSnippetData);
        } else {
          invalidSnippetData.push(itemSnippetData);
        }
      });

      if (!isEmpty(cancelOnlySnippetData)) {
        const validSnippet = replaceTemplatedTextForMultipleItems(decisionNode.itemValidSnippet, cancelOnlySnippetData);
        validSnippet && dispatch(addSnippetText(validSnippet));
      }

      if (!isEmpty(invalidSnippetData)) {
        const invalidSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidSnippet,
          invalidSnippetData
        );
        invalidSnippet && dispatch(addSnippetText(invalidSnippet));
      }

      if (!isEmpty(discountOnlySnippetData)) {
        const discountOnlySnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemDiscountOnlySnippet,
          discountOnlySnippetData
        );
        discountOnlySnippet && dispatch(addSnippetText(discountOnlySnippet));
      }

      if (!isEmpty(cancelOnlySnippetData) || !isEmpty(discountOnlySnippetData)) {
        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (error) {
      handleError({
        error,
        customMessage: 'Error while processing cancellation and refund or discounts',
        showPopup: true,
      });
    } finally {
      dispatch(setLoadingUnique(false, 'searchPage'));
    }
  };
};

export const processRemotelyForCancellationAndRefund = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));

      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const orderId = getSelectedOrderId(state);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
      const selectedItemsIds = getSelectedLineItemsIds(state);
      const customerEmail = getECommerceSearchEmail(state);
      const orderDate = get(getSelectedOrder(state), 'createdAt', null);
      const accountType = get(getSelectedOrder(state), 'extraParams.vendorType', null);

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearInvalidSnippetData());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());
      dispatch(ordersActions.setRefundAmount(0));

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // 3. Get order cancel info
      const cancelPayload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const refundPayload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        orderDate,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const cancelData = (await canCancel(cancelPayload)) || {};
      const refundData = (await canRefund(refundPayload)) || {};

      // 4. Process each item to determine if they qualify for a cancellation and refund
      const validSnippetData = [];
      const invalidSnippetData = [];
      const refundOnlySnippetData = [];
      const cancelOnlySnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemId = getItemId(item, orderingSystem);
        const itemIsEligibleForRefund = getIsItemEligibleForAction(refundData, itemId);
        const itemIsEligibleForCancel = getIsItemEligibleForAction(cancelData, itemId);

        if (itemIsEligibleForCancel && itemIsEligibleForRefund) {
          dispatch(ordersActions.addCancelAndRefundItem(item));
          validSnippetData.push(itemSnippetData);
        } else if (itemIsEligibleForRefund) {
          dispatch(ordersActions.addRefundOnlyItem(item));
          refundOnlySnippetData.push(itemSnippetData);
        } else if (itemIsEligibleForCancel) {
          dispatch(ordersActions.addCancelOnlyItem(item));
          cancelOnlySnippetData.push(itemSnippetData);
        } else {
          invalidSnippetData.push(itemSnippetData);
        }
      });

      if (!isEmpty(validSnippetData)) {
        const validSnippet = replaceTemplatedTextForMultipleItems(decisionNode.itemValidSnippet, validSnippetData);
        validSnippet && dispatch(addSnippetText(validSnippet));
      }
      if (!isEmpty(invalidSnippetData)) {
        const invalidSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidSnippet,
          invalidSnippetData
        );
        invalidSnippet && dispatch(addSnippetText(invalidSnippet));
      }

      if (!isEmpty(refundOnlySnippetData)) {
        const refundOnlySnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemRefundOnlySnippet,
          refundOnlySnippetData
        );
        refundOnlySnippet && dispatch(addSnippetText(refundOnlySnippet));
      }

      if (!isEmpty(cancelOnlySnippetData)) {
        const cancelOnlySnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemCancelOnlySnippet,
          cancelOnlySnippetData
        );
        cancelOnlySnippet && dispatch(addSnippetText(cancelOnlySnippet));
      }

      if (!isEmpty(validSnippetData) || !isEmpty(refundOnlySnippetData) || !isEmpty(cancelOnlySnippetData)) {
        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (e) {
      showError(e);
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const processRemotelyForCancellationAndRefundOrDiscount = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoadingUnique(true, 'searchPage'));

      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const orderId = getSelectedOrderId(state);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
      const selectedItemsIds = getSelectedLineItemsIds(state);
      const customerEmail = getECommerceSearchEmail(state);
      const orderDate = get(getSelectedOrder(state), 'createdAt', null);
      const accountType = get(getSelectedOrder(state), 'extraParams.vendorType', null);

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearInvalidSnippetData());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());
      dispatch(ordersActions.setRefundAmount(0));

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // 3. Get order cancel info
      const cancelPayload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const refundPayload = {
        simplrAccountId,
        orderDate,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const discountPayload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const cancelData = (await canCancel(cancelPayload)) || {};
      const refundData = (await canRefund(refundPayload)) || {};
      const discountData = (await canDiscount(discountPayload)) || {};

      // 4. Process each item to determine if they qualify for a cancellation and refund
      const validSnippetData = [];
      const invalidSnippetData = [];
      const refundOnlySnippetData = [];
      const cancelOnlySnippetData = [];
      const discountOnlySnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemId = getItemId(item, orderingSystem);
        const itemIsEligibleForRefund = getIsItemEligibleForAction(refundData, itemId);
        const itemIsEligibleForCancel = getIsItemEligibleForAction(cancelData, itemId);
        const itemIsEligibleForDiscount = getIsItemEligibleForAction(discountData, itemId);

        if (itemIsEligibleForDiscount) {
          dispatch(ordersActions.addDiscountOnlyItem(item));
          discountOnlySnippetData.push(itemSnippetData);
        } else if (itemIsEligibleForCancel && itemIsEligibleForRefund) {
          dispatch(ordersActions.addCancelAndRefundItem(item));
          validSnippetData.push(itemSnippetData);
        } else if (itemIsEligibleForRefund) {
          dispatch(ordersActions.addRefundOnlyItem(item));
          refundOnlySnippetData.push(itemSnippetData);
        } else if (itemIsEligibleForCancel) {
          dispatch(ordersActions.addCancelOnlyItem(item));
          cancelOnlySnippetData.push(itemSnippetData);
        } else {
          invalidSnippetData.push(itemSnippetData);
        }
      });

      if (!isEmpty(validSnippetData)) {
        const validSnippet = replaceTemplatedTextForMultipleItems(decisionNode.itemValidSnippet, validSnippetData);
        validSnippet && dispatch(addSnippetText(validSnippet));
      }

      if (!isEmpty(invalidSnippetData)) {
        const invalidSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidSnippet,
          invalidSnippetData
        );
        invalidSnippet && dispatch(addSnippetText(invalidSnippet));
      }

      if (!isEmpty(refundOnlySnippetData)) {
        const refundOnlySnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemRefundOnlySnippet,
          refundOnlySnippetData
        );
        refundOnlySnippet && dispatch(addSnippetText(refundOnlySnippet));
      }

      if (!isEmpty(cancelOnlySnippetData)) {
        const cancelOnlySnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemCancelOnlySnippet,
          cancelOnlySnippetData
        );
        cancelOnlySnippet && dispatch(addSnippetText(cancelOnlySnippet));
      }

      if (!isEmpty(discountOnlySnippetData)) {
        const discountOnlySnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemDiscountOnlySnippet,
          discountOnlySnippetData
        );
        discountOnlySnippet && dispatch(addSnippetText(discountOnlySnippet));
      }

      if (
        !isEmpty(validSnippetData) ||
        !isEmpty(refundOnlySnippetData) ||
        !isEmpty(cancelOnlySnippetData) ||
        !isEmpty(discountOnlySnippetData)
      ) {
        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (error) {
      handleError({
        error,
        customMessage: 'Error while processing cancellation and refund or discounts',
        showPopup: true,
      });
    } finally {
      dispatch(setLoadingUnique(false, 'searchPage'));
    }
  };
};

export const processRemotelyForDiscount = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoadingUnique(true, 'searchPage'));

      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const orderId = getSelectedOrderId(state);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
      const selectedItemsIds = getSelectedLineItemsIds(state);
      const customerEmail = getECommerceSearchEmail(state);
      const accountType = get(getSelectedOrder(state), 'extraParams.vendorType', null);

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());
      dispatch(ordersActions.setRefundAmount(0));

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // 3. Get order cancel info
      const payload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const discountData = (await canDiscount(payload)) || {};

      // 4. Process each item to determine if they qualify for a cancellation and refund
      const validSnippetData = [];
      const invalidSnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemId = getItemId(item, orderingSystem);
        const itemIsEligibleForDiscount = getIsItemEligibleForAction(discountData, itemId);

        if (itemIsEligibleForDiscount) {
          dispatch(ordersActions.addDiscountOnlyItem(item));
          validSnippetData.push(itemSnippetData);
        } else {
          invalidSnippetData.push(itemSnippetData);
        }
      });

      if (!isEmpty(validSnippetData)) {
        const validSnippet = replaceTemplatedTextForMultipleItems(decisionNode.itemValidSnippet, validSnippetData);
        validSnippet && dispatch(addSnippetText(validSnippet));
      }

      if (!isEmpty(invalidSnippetData)) {
        const invalidSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidSnippet,
          invalidSnippetData
        );
        invalidSnippet && dispatch(addSnippetText(invalidSnippet));
      }

      if (!isEmpty(validSnippetData)) {
        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (error) {
      handleError({
        error,
        customMessage: 'Error while processing cancellation and refund or discounts',
        showPopup: true,
      });
    } finally {
      dispatch(setLoadingUnique(false, 'searchPage'));
    }
  };
};

export const processRemotelyForRefund = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));

      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const orderId = getSelectedOrderId(state);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const selectedItems = !isEmpty(userSelectedItems) ? userSelectedItems : lineItemsOfSelectedOrder;
      const selectedItemsIds = getSelectedLineItemsIds(state);
      const orderDate = get(getSelectedOrder(state), 'createdAt', null);
      const customerEmail = getECommerceSearchEmail(state);
      const accountType = get(getSelectedOrder(state), 'extraParams.vendorType', null);

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearInvalidSnippetData());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());
      dispatch(ordersActions.setRefundAmount(0));

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // 3. Get order cancel info
      const payload = {
        simplrAccountId,
        orderNumber: String(orderId),
        itemIds: selectedItemsIds,
        orderDate: orderDate,
        ...(customerEmail && { customerEmail }),
        ...(accountType && { accountType }),
      };

      const refundData = (await canRefund(payload)) || {};

      // 4. Process each item to determine if they qualify for a cancellation and refund
      const validSnippetData = [];
      const invalidSnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemId = getItemId(item, orderingSystem);
        /**
         * NOTE(apoorvyadav1|spd-11183|2024-11-05): Chegg specific: we create a
         * formatted string for ineligibilityReason for improved readablity on
         * dashboard when multiple items are ineligible for refund
         */
        const ineligibilityReason = getItemIneligiblityReason(refundData, itemId);
        itemSnippetData.key = itemId;
        itemSnippetData.formattedIneligibilityReason = `${itemSnippetData.itemName}: ${ineligibilityReason}`;
        if (getIsItemEligibleForAction(refundData, itemId)) {
          dispatch(ordersActions.addRefundOnlyItem(item));
          validSnippetData.push(itemSnippetData);
        } else {
          invalidSnippetData.push(itemSnippetData);
        }
      });

      if (!isEmpty(validSnippetData)) {
        const validSnippet = replaceTemplatedTextForMultipleItems(decisionNode.itemValidSnippet, validSnippetData);
        validSnippet && dispatch(addSnippetText(validSnippet));
      }
      if (!isEmpty(invalidSnippetData)) {
        const invalidSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidSnippet,
          invalidSnippetData
        );
        invalidSnippetData && dispatch(setInvalidSnippetData(invalidSnippetData));
        invalidSnippet && dispatch(addSnippetText(invalidSnippet));
      }

      if (!isEmpty(validSnippetData)) {
        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (e) {
      showError(e);
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};
